import DEBUG, {MOUNT_CLASS_TO} from '@config/debug';
import EventListenerBase from '@helpers/eventListenerBase';
import noop from '@helpers/noop';
import {nextRandomUint} from '@helpers/random';
import {DataJSON, GroupCall, InputGroupCall} from '@layer';
import {JoinGroupCallJsonPayload} from '@appManagers/appGroupCallsManager';
import {AppManagers} from '@lib/managers';
import apiManagerProxy from '@lib/apiManagerProxy';
import rootScope from '@lib/rootScope';
import {RTMP_UNIFIED_CHANNEL_ID, RTMP_UNIFIED_QUALITY} from '@lib/calls/constants';
import {groupCallToInput} from '@lib/calls/helpers/groupCallUpdates';
import RTMP_STATE from '@lib/calls/rtmpState';
import {logger} from '@lib/logger';
import callTransitionCoordinator from '@lib/calls/callTransitionCoordinator';

const log = logger('RTMP');

export class RtmpCallInstance extends EventListenerBase<{
  state: (state: RTMP_STATE) => void
}> {
  public chatId: ChatId;
  public peerId: PeerId;
  public call: GroupCall.groupCall;
  public inputCall: InputGroupCall.inputGroupCall;
  public ssrc: number;
  public pip: boolean;
  public admin: boolean;
  public lastKnownTime: string;

  private _state: RTMP_STATE;

  constructor(params: {
    chatId: ChatId,
    peerId: PeerId,
    call: GroupCall.groupCall,
    inputCall: InputGroupCall.inputGroupCall,
    ssrc: number,
    pip: boolean,
    admin: boolean,
    lastKnownTime: string
  }) {
    super(false);
    Object.assign(this, params);
    this._state = RTMP_STATE.CONNECTING;
  }

  public cleanup() {
    super.cleanup();
  }

  public get state() {
    return this._state;
  }

  public set state(value: RTMP_STATE) {
    this._state = value;
    this.dispatchEvent('state', value);
  }
}

export class RtmpCallsController extends EventListenerBase<{
  startedJoining: (peerId: PeerId) => void,
  currentCallChanged: (call: RtmpCallInstance) => void
}> {
  private managers: AppManagers;

  private _currentCall: RtmpCallInstance;
  private currentCallGeneration = 0;
  private rejoinPromise?: Promise<void>;

  public construct(managers: AppManagers) {
    this.managers = managers;

    rootScope.addEventListener('group_call_update', this.onGroupCallUpdate);

    apiManagerProxy.serviceMessagePort.addEventListener('rtmpStreamTime', ({callId, time}) => {
      if(this.currentCall?.call.id === callId) {
        this.currentCall.lastKnownTime = time;
      }
    });

    if(typeof(window) !== 'undefined') {
      // Watching a stream is a server-side membership (phone.joinGroupCall).
      // Leave it when the tab goes away — best effort through the manager,
      // which the SharedWorker keeps alive while any other tab exists — so the
      // viewer count does not carry a ghost until the server times it out.
      window.addEventListener('pagehide', () => {
        const {currentCall} = this;
        if(currentCall) {
          void this.managers.appGroupCallsManager.hangUp(currentCall.call.id, currentCall.ssrc).catch(noop);
        }
      });
    }
  }

  public get currentCall() {
    return this._currentCall;
  }

  private set currentCall(value: RtmpCallInstance | undefined) {
    if(this.currentCall === value) {
      return;
    }

    this._currentCall?.cleanup();
    ++this.currentCallGeneration;
    this.dispatchEvent('currentCallChanged', this._currentCall = value);
  }

  private onGroupCallUpdate = (update: GroupCall) => {
    if(update.id !== this.currentCall?.call.id) return;

    if(update._ === 'groupCallDiscarded') {
      this.currentCall = undefined;
      return;
    }

    this.currentCall.call = update;
    this.dispatchEvent('currentCallChanged', this.currentCall);
  };

  private randomSsrc() {
    // random signed int32
    return (Math.random() < 0.5 ? -1 : 1) * nextRandomUint(16);
  }

  private getJoinPayload(ssrc: number): DataJSON {
    const innerData: JoinGroupCallJsonPayload = {
      'fingerprints': [],
      'pwd': '',
      'ssrc': ssrc,
      'ssrc-groups': [],
      'ufrag': ''
    };

    return {
      _: 'dataJSON',
      data: JSON.stringify(innerData)
    };
  }

  public async joinCall(chatId: ChatId) {
    if(this.currentCall) {
      throw new Error('Already in rtmp call');
    }

    const peerId = chatId.toPeerId(true);
    this.dispatchEvent('startedJoining', peerId);

    const ssrc = this.randomSsrc();
    const data = this.getJoinPayload(ssrc);

    const chat = await this.managers.appProfileManager.getChatFull(chatId);
    if(chat._ !== 'channelFull') {
      throw new Error('Not a chat');
    }

    const callId = (chat.call as InputGroupCall.inputGroupCall)?.id;
    if(!callId) {
      throw new Error('No call id');
    }

    const call = await this.managers.appGroupCallsManager.getGroupCallFull(callId);
    if(call._ !== 'groupCall') {
      throw new Error('Not a group call');
    }

    const update = await this.managers.appGroupCallsManager.joinGroupCall(callId, data, {type: 'main'});
    let instance: RtmpCallInstance;
    try {
      const updateData = JSON.parse(update.params.data);
      if(updateData.rtmp !== true) {
        throw new Error('Not an rtmp call');
      }

      instance = new RtmpCallInstance({
        call,
        inputCall: groupCallToInput(call),
        chatId,
        peerId,
        ssrc,
        pip: false,
        admin: Boolean(chat.pFlags?.can_delete_channel),
        lastKnownTime: '0'
      });
    } catch(err) {
      await this.compensateAcceptedJoin(update.acceptedCallInput, ssrc, 'initial RTMP join');
      throw err;
    }

    this.currentCall = instance;
  }

  public async leaveCall(discard = false, expectedCall?: RtmpCallInstance): Promise<boolean> {
    const currentCall = this.currentCall;
    if(!currentCall || (expectedCall && currentCall !== expectedCall)) return false;

    this.currentCall = undefined;
    apiManagerProxy.serviceMessagePort.invokeVoid('leaveRtmpCall', [currentCall.call.id, true]);
    await this.managers.appGroupCallsManager.hangUp(currentCall.call.id, discard ? true : currentCall.ssrc);
    return true;
  }

  public async isCurrentCallDead(checkJoined = false, triedRejoin = false): Promise<'dead' | 'dying' | 'alive'> {
    if(!this.currentCall) return 'dead';

    const state = await this.managers.appGroupCallsManager.fetchRtmpState(this.currentCall.inputCall);
    if(!checkJoined) return state.channels.length === 0 ? 'dead' : 'alive';

    // check if we are joined by trying to fetch a part
    const unified = state.channels.find((it) => it.channel === RTMP_UNIFIED_CHANNEL_ID);
    if(!unified) return 'dead';
    try {
      const time = this.currentCall.lastKnownTime === '0' ? unified.last_timestamp_ms : this.currentCall.lastKnownTime;
      await this.managers.appGroupCallsManager.fetchRtmpPart({
        _: 'inputGroupCallStream',
        call: this.currentCall.inputCall,
        video_channel: RTMP_UNIFIED_CHANNEL_ID,
        video_quality: RTMP_UNIFIED_QUALITY,
        scale: unified.scale,
        time_ms: time
      }, state.dcId);
      return 'alive';
    } catch(e) {
      if((e as ApiError).type === 'GROUPCALL_JOIN_MISSING' && !triedRejoin) {
        try {
          await this.rejoinCall();
          return await this.isCurrentCallDead(true, true);
        } catch(err) {
          log.error('rejoinCall failed during dead-call check', err);
        }
      }
    }

    return 'dying';
  }

  public rejoinCall(): Promise<void> {
    if(this.rejoinPromise) return this.rejoinPromise;

    // Only recovery enters the shared call-transition FIFO here. User joins are
    // already wrapped by AppImManager; wrapping joinCall itself would enqueue a
    // transition from inside another transition and deadlock it against itself.
    const promise = callTransitionCoordinator.run(() => this.rejoinCallInternal()).finally(() => {
      if(this.rejoinPromise === promise) this.rejoinPromise = undefined;
    });
    this.rejoinPromise = promise;
    return promise;
  }

  private async rejoinCallInternal(): Promise<void> {
    const currentCall = this.currentCall;
    if(!currentCall) return;

    const generation = this.currentCallGeneration;
    const ssrc = this.randomSsrc();
    const data = this.getJoinPayload(ssrc);
    const update = await this.managers.appGroupCallsManager.joinGroupCall(currentCall.call.id, data, {
      type: 'main',
      // Preserve the exact call identity used by this recovery so a late accepted
      // join can be compensated even after caches/currentCall have moved on.
      e2eCallInput: currentCall.inputCall
    });

    if(this.currentCall !== currentCall || this.currentCallGeneration !== generation) {
      await this.compensateAcceptedJoin(update.acceptedCallInput, ssrc, 'late RTMP rejoin');
      return;
    }

    // Do not publish the replacement source before acceptance. A concurrent
    // direct leave must still remove the OLD membership; if this response then
    // arrives late, the generation branch above removes the newly accepted one.
    currentCall.ssrc = ssrc;
  }

  private async compensateAcceptedJoin(call: InputGroupCall, ssrc: number, context: string): Promise<void> {
    try {
      await this.managers.appGroupCallsManager.leaveGroupCall(call, ssrc);
    } catch(err) {
      log.error(`${context} compensation failed`, err);
    }
  }
}

const rtmpCallsController = new RtmpCallsController();
DEBUG && (MOUNT_CLASS_TO.rtmpCallsController = rtmpCallsController);
export default rtmpCallsController;
